import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { getFlightDetailsWithDedup } from 'api/order'
import { formatAddedBaggage } from 'checkout/lib/utils/flights/format'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { getAddedBaggageFromAddons, getLegInclusions } from 'lib/flights/flightUtils'
import { type TransportOption, isLeFlightSavedItemData } from 'tripPlanner/api/types'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import BookedShortlistedFlightDetailsDrawer from './BookedShortlistedFlightDetailsDrawer'
import ShortlistedFlightDetailsDrawer from './ShortlistedFlightDetailsDrawer'
import type {
  BookedShortlistedFlightDetailsSection,
  ShortlistedFlightDetailsSection,
} from './types'
import type { RefreshedFlight } from './useRefreshShortlistedFlights'
import { getCachedJourney } from './shortlistedJourneyCache'
import { buildJourneyFromSavedFlight } from './buildSavedFlightJourney'
import type { BookFlightResult } from 'tripPlanner/components/Chat/Flights/flightRecommendationBookingTypes'
import { resolveShortlistedFlightPriceAndCurrency } from './flightShortlistUtils'

interface UseViewBookedResult {
  viewBookedFlightDetails: (option: TransportOption) => void
  viewUnbookedFlightDetails: (
    option: TransportOption,
    refreshedData?: RefreshedFlight,
    onBookNow?: (option: TransportOption) => void | Promise<BookFlightResult>,
    cartFlightItem?: App.MultiCartFlightItem,
    options?: { suppressUnavailableMessage?: boolean },
  ) => Promise<boolean>
  viewingDetailsItemId: number | undefined
  isFlightDetailsDrawerOpen: boolean
  closeFlightDetailsDrawer: () => void
  flightDetailsDrawer: ReactNode
}

interface FlightOrderDetailsTarget {
  orderId?: string
  orderFlightItemId?: string
  preferredLegIndex?: number
}

interface BookedFlightDetailsContext {
  isReturn: boolean
  targets: Array<FlightOrderDetailsTarget>
}

type FlightDetailsDrawerState = {
  kind: 'booked'
  isOpen: boolean
  option: TransportOption
  sections: Array<BookedShortlistedFlightDetailsSection>
} | {
  kind: 'unbooked'
  isOpen: boolean
  option: TransportOption
  sections: Array<ShortlistedFlightDetailsSection>
  price?: number
  currencyCode?: string
  regularPrice?: number
  isLuxPlusFare?: boolean
  onBookNow?: (option: TransportOption) => void | Promise<BookFlightResult>
}

function getBookedFlightDetailsContext(option: TransportOption): BookedFlightDetailsContext {
  const outboundData = isLeFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  const returnData = isLeFlightSavedItemData(option.returnLeg?.savedItemData) ? option.returnLeg.savedItemData : undefined

  const isReturn = Boolean(option.returnLeg && returnData)
  const targets: Array<FlightOrderDetailsTarget> = []
  const seenTargets = new Set<string>()

  const addTarget = (target?: FlightOrderDetailsTarget) => {
    if (!target?.orderId || !target?.orderFlightItemId) {
      return
    }

    const targetKey = `${target.orderId}-${target.orderFlightItemId}-${target.preferredLegIndex ?? 'none'}`
    if (seenTargets.has(targetKey)) {
      return
    }

    seenTargets.add(targetKey)
    targets.push(target)
  }

  addTarget({
    orderId: outboundData?.orderId,
    orderFlightItemId: outboundData?.orderFlightItemId,
    preferredLegIndex: outboundData?.legOrder,
  })
  addTarget({
    orderId: returnData?.orderId,
    orderFlightItemId: returnData?.orderFlightItemId,
    preferredLegIndex: returnData?.legOrder,
  })

  return { isReturn, targets }
}

function getSafeLegIndex(legs: Array<App.Leg>, preferredLegIndex?: number): number | undefined {
  if (legs.length === 0) {
    return undefined
  }

  if (typeof preferredLegIndex === 'number' && legs[preferredLegIndex]) {
    return preferredLegIndex
  }

  return 0
}

function getLegModalIndices({
  legs,
  preferredLegIndex,
  isReturn,
}: {
  legs: Array<App.Leg>
  preferredLegIndex?: number
  isReturn: boolean
}): Array<number> {
  const firstIndex = getSafeLegIndex(legs, preferredLegIndex)

  if (firstIndex == null) {
    return []
  }

  if (isReturn && legs[0] && legs[1]) {
    return [0, 1]
  }

  if (!isReturn || legs.length === 1) {
    return [firstIndex]
  }

  const secondIndex = firstIndex === 0 ? 1 : 0
  if (!legs[secondIndex]) {
    return [firstIndex]
  }

  return [firstIndex, secondIndex]
}

function getHydratedJourneys(option: TransportOption, refreshedData?: RefreshedFlight): {
  isReturn: boolean
  departingJourney?: App.JourneyV2
  returningJourney?: App.JourneyV2
} {
  const savedItemData = isLeFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  const returnLegData = isLeFlightSavedItemData(option.returnLeg?.savedItemData) ? option.returnLeg.savedItemData : undefined

  const isReturn = Boolean(option.returnLeg && returnLegData)

  const cachedDepartingJourney = savedItemData?.journeyKey
    ? getCachedJourney(savedItemData.journeyKey)?.journey
    : undefined
  const cachedReturningJourney = returnLegData?.journeyKey
    ? getCachedJourney(returnLegData.journeyKey)?.journey
    : undefined

  // The journey cache is only warm for flights the shortlist or a live search
  // put there. A flight added straight from a chat carousel misses it, and
  // without a journey the drawer degrades to the cart leg extension — the same
  // row the tile already shows. Rebuild from saved data so those flights get the
  // full JourneyV2 details instead.
  const departingJourney = refreshedData?.matchedDepartingJourney
    ?? refreshedData?.matchedJourney
    ?? cachedDepartingJourney
    ?? (savedItemData ? buildJourneyFromSavedFlight(savedItemData) : undefined)

  const returningJourney = refreshedData?.matchedReturningJourney
    ?? cachedReturningJourney
    ?? (returnLegData ? buildJourneyFromSavedFlight(returnLegData) : undefined)

  return { isReturn, departingJourney, returningJourney }
}

function getCartFlightDetailsSections(
  cartFlightItem: App.MultiCartFlightItem | undefined,
): Array<ShortlistedFlightDetailsSection> {
  if (!cartFlightItem || !isFlightItem(cartFlightItem.item)) return []
  const checkoutFlightItem = cartFlightItem.item

  return checkoutFlightItem.flights.map((flight, index) => ({
    key: [
      `cart-flight-${index + 1}`,
      flight.journeyId,
      flight.journeyKey,
      flight.departingAirportCode,
      flight.arrivalAirportCode,
      flight.departureDateTime,
    ].filter(Boolean).join('|'),
    cartFlight: flight,
    isClassicReward: checkoutFlightItem.isClassicReward,
  }))
}

function useViewBookedShortlistedFlight(): UseViewBookedResult {
  const intl = useIntl()
  const [viewingDetailsItemId, setViewingDetailsItemId] = useState<number | undefined>(undefined)
  const [drawerState, setDrawerState] = useState<FlightDetailsDrawerState | undefined>(undefined)
  const detailRequestIdRef = useRef(0)

  const startDetailRequest = useCallback(() => {
    detailRequestIdRef.current += 1
    return detailRequestIdRef.current
  }, [])

  const isLatestDetailRequest = useCallback((requestId: number) => {
    return detailRequestIdRef.current === requestId
  }, [])

  const closeFlightDetailsDrawer = useCallback(() => {
    detailRequestIdRef.current += 1
    setDrawerState(current => current ? { ...current, isOpen: false } : current)
  }, [])

  const clearFlightDetailsDrawer = useCallback(() => {
    detailRequestIdRef.current += 1
    setDrawerState(undefined)
    setViewingDetailsItemId(undefined)
  }, [])

  const viewBookedFlightDetails = useCallback(async(option: TransportOption) => {
    const requestId = startDetailRequest()
    setViewingDetailsItemId(undefined)
    const { targets, isReturn } = getBookedFlightDetailsContext(option)

    if (targets.length === 0) {
      showSnackbar(intl.formatMessage({
        id: 'Fylku-gtUXFgXElsmmcQ',
        defaultMessage: 'Flight details are unavailable for this booked flight.',
        description: 'Warning shown when a booked shortlisted flight is missing order identifiers for flight details modal',
      }), 'warning')
      return
    }

    setViewingDetailsItemId(option.itemId)
    let keepItemBusyUntilDrawerCloses = false

    try {
      const sections: Array<BookedShortlistedFlightDetailsSection> = []

      for (const [targetIndex, target] of targets.entries()) {
        if (!target.orderId || !target.orderFlightItemId) {
          continue
        }

        const flightDetails = await getFlightDetailsWithDedup({
          orderId: target.orderId,
          itemId: target.orderFlightItemId,
        })

        const legs = flightDetails.bookingJourney?.legs ?? []
        const legIndices = getLegModalIndices({
          legs,
          preferredLegIndex: target.preferredLegIndex,
          isReturn: isReturn && targets.length === 1,
        })

        for (const legIndex of legIndices) {
          const selectedLeg = legs[legIndex]
          if (!selectedLeg) {
            continue
          }

          const legInclusions = getLegInclusions(selectedLeg.inclusions)
          const addedBaggage = getAddedBaggageFromAddons(selectedLeg.addons)
          const addedBaggageFormatted = formatAddedBaggage(addedBaggage)

          sections.push({
            key: `${target.orderId}-${target.orderFlightItemId}-${legIndex}`,
            leg: selectedLeg,
            inclusions: [...legInclusions, ...addedBaggageFormatted],
            reservationInfo: flightDetails.reservationInfo,
            pnrId: flightDetails.pnrId,
            region: flightDetails.region,
            isStandalone: Boolean(flightDetails.metadata?.isStandalone),
            isDeparting: isReturn ? targetIndex === 0 && legIndex === 0 : legIndex === 0,
          })
        }
      }

      if (!isLatestDetailRequest(requestId)) {
        return
      }

      if (sections.length === 0) {
        showSnackbar(intl.formatMessage({
          id: 'NWaACR3EZZIYneftyj1S',
          defaultMessage: 'Unable to load flight details right now. Please try again.',
          description: 'Error message when no flight leg data is available after loading booked shortlisted flight details',
        }), 'critical')
        return
      }

      setDrawerState({
        kind: 'booked',
        isOpen: true,
        option,
        sections,
      })
      keepItemBusyUntilDrawerCloses = true
    } catch {
      if (isLatestDetailRequest(requestId)) {
        showSnackbar(intl.formatMessage({
          id: 'zIKPyY55c_9sh16J9cyM',
          defaultMessage: 'Unable to load flight details right now. Please try again.',
          description: 'Error snackbar shown when fetching booked shortlisted flight details fails',
        }), 'critical')
      }
    } finally {
      if (!keepItemBusyUntilDrawerCloses && isLatestDetailRequest(requestId)) {
        setViewingDetailsItemId(undefined)
      }
    }
  }, [intl, isLatestDetailRequest, startDetailRequest])

  const viewUnbookedFlightDetails = useCallback(async(
    option: TransportOption,
    refreshedData?: RefreshedFlight,
    onBookNow?: (option: TransportOption) => void | Promise<BookFlightResult>,
    cartFlightItem?: App.MultiCartFlightItem,
    options?: { suppressUnavailableMessage?: boolean },
  ) => {
    const requestId = startDetailRequest()
    setViewingDetailsItemId(undefined)
    const { isReturn, departingJourney, returningJourney } = getHydratedJourneys(option, refreshedData)

    if (!departingJourney || (isReturn && !returningJourney)) {
      const cartSections = getCartFlightDetailsSections(cartFlightItem)
      if (cartSections.length > 0) {
        const checkoutFlightItem = cartFlightItem && isFlightItem(cartFlightItem.item)
          ? cartFlightItem.item
          : undefined

        if (!isLatestDetailRequest(requestId)) {
          return false
        }

        setDrawerState({
          kind: 'unbooked',
          isOpen: true,
          option,
          sections: cartSections,
          price: checkoutFlightItem?.totalFare,
        })
        return true
      }

      if (!options?.suppressUnavailableMessage) {
        showSnackbar(intl.formatMessage({
          id: '_f4JZXjCfPtWLFHAqf1x',
          defaultMessage: 'Flight details are unavailable for this flight right now.',
          description: 'Warning shown when a non-booked shortlisted flight does not have hydrated journey data for details drawer',
        }), 'warning')
      }
      return false
    }

    // Details and the saved card share one quote and passenger basis. Hydrated
    // journeys are for route/fare details, not an adult-price fallback.
    const savedItemData = isLeFlightSavedItemData(option.savedItemData)
      ? option.savedItemData
      : undefined
    const { displayPrice: price, priceCurrency: priceCurrencyCode, regularPrice, isLuxPlusFare } = resolveShortlistedFlightPriceAndCurrency({
      isReturn,
      savedItemData,
      refreshedData,
    })

    setViewingDetailsItemId(option.itemId)
    let keepItemBusyUntilDrawerCloses = false

    try {
      const sections: Array<ShortlistedFlightDetailsSection> = [
        {
          key: `${departingJourney.bookingInfo.journeyKey ?? departingJourney.id}-departing`,
          journey: departingJourney,
        },
      ]

      if (isReturn && returningJourney) {
        sections.push({
          key: `${returningJourney.bookingInfo.journeyKey ?? returningJourney.id}-returning`,
          journey: returningJourney,
        })
      }

      if (!isLatestDetailRequest(requestId)) {
        return false
      }

      setDrawerState({
        kind: 'unbooked',
        isOpen: true,
        option,
        sections,
        price,
        currencyCode: priceCurrencyCode,
        regularPrice,
        isLuxPlusFare,
        onBookNow,
      })
      keepItemBusyUntilDrawerCloses = true
      return true
    } finally {
      if (!keepItemBusyUntilDrawerCloses && isLatestDetailRequest(requestId)) {
        setViewingDetailsItemId(undefined)
      }
    }
  }, [intl, isLatestDetailRequest, startDetailRequest])

  const flightDetailsDrawer = useMemo(() => {
    if (!drawerState) return null

    if (drawerState.kind === 'booked') {
      return (
        <BookedShortlistedFlightDetailsDrawer
          isOpen={drawerState.isOpen}
          option={drawerState.option}
          sections={drawerState.sections}
          onClose={closeFlightDetailsDrawer}
          onAfterClose={clearFlightDetailsDrawer}
        />
      )
    }

    return (
      <ShortlistedFlightDetailsDrawer
        isOpen={drawerState.isOpen}
        option={drawerState.option}
        sections={drawerState.sections}
        price={drawerState.price}
        currencyCode={drawerState.currencyCode}
        regularPrice={drawerState.regularPrice}
        isLuxPlusFare={drawerState.isLuxPlusFare}
        onBookNow={drawerState.onBookNow}
        onClose={closeFlightDetailsDrawer}
        onAfterClose={clearFlightDetailsDrawer}
      />
    )
  }, [clearFlightDetailsDrawer, closeFlightDetailsDrawer, drawerState])

  return {
    viewBookedFlightDetails,
    viewUnbookedFlightDetails,
    viewingDetailsItemId,
    isFlightDetailsDrawerOpen: Boolean(drawerState?.isOpen),
    closeFlightDetailsDrawer,
    flightDetailsDrawer,
  }
}

export default useViewBookedShortlistedFlight
